import React, { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  calculateTotalPriceFooter,
  calculateUnitPrice,
  getDefaultValues,
  getVatRateOptions,
} from './utils/utils'
import { useLocale } from '../../../../../../../hooks/use-locale'
import {
  ClientPricingItemTypeEnum,
  CurrencyAreaOutputDto,
  ServiceProviderCategoryEnum,
  ServiceProvisionUnitEnum,
} from '../../../../../../../lib/graphql'
import { Button } from '../../../../../../../shadcn/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../../../../shadcn/components/ui/form'
import { Input } from '../../../../../../../shadcn/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../shadcn/components/ui/select'
import { Textarea } from '../../../../../../../shadcn/components/ui/textarea'
import { provisionUnitToLabel } from '../../../../../../../utils'
import {
  divideByFactor,
  divideByFactorRate,
  multiplyByFactor,
} from '../../../../../../../utils/prices'
import { VAT_STATUS } from '../../../../../../contexts'
import { useVatStatus } from '../../../../../../contexts/vat-status-context/useVatStatus'
import InputPrice from '../../../../../../forms/new-forms/InputPrice'
import { Modal, TotalPrice } from '../../../../../../molecules'
import { IProposalInteractionsProps } from '../../../../../../organisms/proposal/Proposal'

const DEFAULT_TAXES_OWNER_FEES_RATE = 0

export interface IQuoteProvisionInputProps {
  _id?: string
  name: string
  description: string
  category: ServiceProviderCategoryEnum
  vatStatus: VAT_STATUS
  unitPrice?: string
  vatRateIndex?: string
  quantity?: string
  unit?: ServiceProvisionUnitEnum
  totalPrice: string | undefined
  isDuplicated?: boolean
  type: ClientPricingItemTypeEnum
}

interface ICreateOrUpdateQuoteProvisionModalProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  currencyArea: CurrencyAreaOutputDto
  clientProposalId: string
  ownerActions: IProposalInteractionsProps['ownerActions']
  defaultValues?: IQuoteProvisionInputProps
  ownerFeesRate: number
}

const CreateOrUpdateQuoteProvisionModal = ({
  isOpen,
  setIsOpen,
  currencyArea,
  clientProposalId,
  ownerActions,
  defaultValues,
  ownerFeesRate,
}: ICreateOrUpdateQuoteProvisionModalProps) => {
  const intl = useIntl()
  const { locale } = useLocale()
  const { vatStatus, setVatStatus } = useVatStatus()
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  const vatRateOptions = useMemo(
    () => getVatRateOptions({ currencyArea, locale }),
    [currencyArea, locale],
  )

  const form = useForm<IQuoteProvisionInputProps>({
    mode: 'onChange',
    defaultValues: getDefaultValues(defaultValues) ?? {
      _id: undefined,
      name: '',
      description: '',
      category: undefined,
      vatStatus,
      unitPrice: undefined,
      vatRateIndex: undefined,
      quantity: undefined,
      unit: undefined,
      totalPrice: '',
    },
  })

  const { setValue, trigger, register } = form

  const formValues = form.watch()
  const inputVatRate = formValues.vatRateIndex
    ? (vatRateOptions.at(parseInt(formValues.vatRateIndex))?.value ?? [])
    : []
  const inputUnitPrice = formValues.unitPrice
    ? parseFloat(formValues.unitPrice)
    : undefined
  const inputQuantity = formValues.quantity
    ? parseInt(formValues.quantity)
    : undefined
  const inputTotalPrice =
    formValues.totalPrice && formValues.totalPrice !== '-'
      ? parseFloat(formValues.totalPrice)
      : undefined
  const isPriceWithVat = formValues.vatStatus === VAT_STATUS.TTC
  const selectedProvisionOwnerFeesRate =
    formValues.category === ServiceProviderCategoryEnum.TAXES
      ? DEFAULT_TAXES_OWNER_FEES_RATE
      : ownerFeesRate

  const validateTotalPrice = async () => {
    try {
      if (inputUnitPrice && inputQuantity && inputTotalPrice) {
        const isValid = await ownerActions?.checkMultiVatRatesValidity.callback(
          {
            clientProposalId,
            quoteProvisionInputs: {
              isSavedWithVat: isPriceWithVat,
              unitPrice: Math.round(multiplyByFactor(inputUnitPrice)),
              quantity: inputQuantity,
              totalPrice: Math.round(multiplyByFactor(inputTotalPrice)),
              vatRates: inputVatRate,
            },
          },
        )

        if (!isValid) {
          return false
        } else {
          return true
        }
      }
    } catch {
      return false
    }
  }

  register('totalPrice', { validate: validateTotalPrice })

  useEffect(() => {
    const newVatStatus = !isPriceWithVat ? VAT_STATUS.HT : VAT_STATUS.TTC
    setVatStatus(newVatStatus)
  }, [isPriceWithVat])

  useEffect(() => {
    if (
      inputUnitPrice !== undefined &&
      inputQuantity &&
      inputQuantity > 0 &&
      inputVatRate.length === 1
    ) {
      let totalPrice
      if (isPriceWithVat) {
        totalPrice =
          Math.round(
            ((inputUnitPrice * inputQuantity) /
              (1 + divideByFactorRate(inputVatRate[0]))) *
              100,
          ) / 100
      } else {
        totalPrice =
          Math.round(
            inputUnitPrice *
              inputQuantity *
              (1 + divideByFactorRate(inputVatRate[0])) *
              100,
          ) / 100
      }
      setValue('totalPrice', totalPrice.toString())
    } else if (formValues.totalPrice && inputVatRate.length !== 2) {
      setValue('totalPrice', '-')
    }
  }, [inputUnitPrice, inputQuantity, inputVatRate, isPriceWithVat])

  const onSubmit = (inputs: IQuoteProvisionInputProps) => {
    if (
      inputs.quantity === undefined ||
      inputs.unitPrice === undefined ||
      inputs.unit === undefined ||
      inputs.vatRateIndex === undefined
    ) {
      throw new Error('Form inputs are not valid')
    }

    const vatRates =
      vatRateOptions.at(parseInt(inputs.vatRateIndex))?.value ?? []

    const quoteProvision = {
      category: inputs.category,
      description: inputs.description,
      isSavedWithVat: inputs.vatStatus === VAT_STATUS.TTC,
      name: inputs.name,
      quantity: parseInt(inputs.quantity),
      unit: inputs.unit,
      unitPrice: Math.round(multiplyByFactor(inputs.unitPrice)),
      vatRates,
      totalPrice: inputs.totalPrice
        ? Math.round(multiplyByFactor(inputs.totalPrice))
        : undefined,
      ...(inputs.isDuplicated && { isDuplicated: inputs.isDuplicated }),
    }

    if (inputs._id) {
      ownerActions?.ownerUpdatedQuoteProvision.callback({
        clientProposalId,
        quoteProvisionInputs: { ...quoteProvision, _id: inputs._id },
      })
    } else {
      ownerActions?.ownerCreatedQuoteProvision.callback({
        clientProposalId,
        quoteProvisionInputs: quoteProvision,
      })
    }

    setIsOpen(false)
  }

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }
    if (inputUnitPrice && inputQuantity && inputTotalPrice) {
      debounceTimeout.current = setTimeout(() => {
        trigger('totalPrice')
      }, 400)
    }
  }, [
    inputTotalPrice,
    inputUnitPrice,
    inputQuantity,
    inputVatRate,
    isPriceWithVat,
  ])

  const unitPriceCalculated = calculateUnitPrice({
    totalPrice: inputTotalPrice,
    quantity: inputQuantity,
  })

  const { totalPriceHT, totalPriceTTC } = calculateTotalPriceFooter({
    inputTotalPrice,
    inputUnitPrice,
    inputQuantity,
    isPriceWithVat,
    currency: currencyArea.currency,
  })

  const unitPriceLabelHT = (
    <FormattedMessage
      id='ui.CreateOrUpdateQuoteProvisionModal.unitPriceLabelHT'
      defaultMessage='Prix unit. HT'
    />
  )
  const unitPriceLabelTTC = (
    <FormattedMessage
      id='ui.CreateOrUpdateQuoteProvisionModal.unitPriceLabelTTC'
      defaultMessage='Prix unit. TTC'
    />
  )

  const writeHereLabelPlaceholder = intl.formatMessage({
    id: 'ui.CreateOrUpdateQuoteProvisionModal.writeHerePlaceholder',
    defaultMessage: 'Écrire ici',
  })

  const choseLabelPlaceholder = intl.formatMessage({
    id: 'ui.CreateOrUpdateQuoteProvisionModal.chosePlaceholder',
    defaultMessage: 'Choisir',
  })

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => setIsOpen(false)}
      className='!min-w-[1000px]'
      customHeader={
        <div className='p-6'>
          <h1 className='text-md font-semibold'>
            <FormattedMessage
              id='ui.quoteSection.createOrUpdateQuoteProvisionModal.header.title'
              defaultMessage='{titlePrefix} une prestation'
              values={{
                titlePrefix: !defaultValues?._id ? (
                  <FormattedMessage
                    id='ui.quoteSection.createOrUpdateQuoteProvisionModal.header.title.add'
                    defaultMessage='Ajouter'
                  />
                ) : (
                  <FormattedMessage
                    id='ui.quoteSection.createOrUpdateQuoteProvisionModal.header.title.edit'
                    defaultMessage='Modifier'
                  />
                ),
              }}
            />
          </h1>
          <h1>
            <FormattedMessage
              id='ui.quoteSection.createOrUpdateQuoteProvisionModal.header.nabooCommission'
              defaultMessage='Commission Naboo : {selectedProvisionOwnerFeesRate} % du total HT'
              values={{
                selectedProvisionOwnerFeesRate: divideByFactor(
                  selectedProvisionOwnerFeesRate,
                ),
              }}
            />
          </h1>
        </div>
      }
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='flex flex-col gap-6'
        >
          <div className='flex justify-between gap-6'>
            <FormField
              control={form.control}
              name='name'
              render={({ field, fieldState }) => (
                <FormItem className='w-1/2'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.name.label'
                      defaultMessage='Nom de la prestation'
                    />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={writeHereLabelPlaceholder}
                      {...field}
                      error={fieldState.error}
                    />
                  </FormControl>
                </FormItem>
              )}
              rules={{ required: true }}
            />
            <FormField
              control={form.control}
              name='category'
              render={({ field, fieldState }) => (
                <FormItem className='w-1/2'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.label'
                      defaultMessage='Catégorie de la prestation'
                    />
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger error={fieldState.error}>
                        <SelectValue placeholder={choseLabelPlaceholder} />
                      </SelectTrigger>

                      <SelectContent className='z-[100000]'>
                        <SelectItem
                          value={ServiceProviderCategoryEnum.BATCH_PROVISIONS}
                        >
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.selectOption.packages'
                            defaultMessage='Forfaits'
                          />
                        </SelectItem>
                        <SelectItem value={ServiceProviderCategoryEnum.HOUSE}>
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.selectOption.house'
                            defaultMessage='Lieu'
                          />
                        </SelectItem>
                        <SelectItem
                          value={ServiceProviderCategoryEnum.RESTAURATION}
                        >
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.selectOption.restoration'
                            defaultMessage='Restauration'
                          />
                        </SelectItem>
                        <SelectItem
                          value={ServiceProviderCategoryEnum.TRANSPORT}
                        >
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.selectOption.transport'
                            defaultMessage='Transport'
                          />
                        </SelectItem>
                        <SelectItem
                          value={ServiceProviderCategoryEnum.ACTIVITY}
                        >
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.selectOption.activity'
                            defaultMessage='Activités'
                          />
                        </SelectItem>
                        <SelectItem value={ServiceProviderCategoryEnum.TAXES}>
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.category.selectOption.taxes'
                            defaultMessage='Taxes'
                          />
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: true }}
            />
          </div>

          <div className='flex items-end justify-between gap-6'>
            <FormField
              control={form.control}
              name='vatStatus'
              render={({ field }) => (
                <FormItem className='w-[15%]'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.vatStatus.label'
                      defaultMessage='Prix en :'
                    />
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent className='z-[100000]'>
                        <SelectItem value={VAT_STATUS.HT}>
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.vatStatus.option.ht'
                            defaultMessage='HT'
                          />
                        </SelectItem>
                        <SelectItem value={VAT_STATUS.TTC}>
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.vatStatus.option.ttc'
                            defaultMessage='TTC'
                          />
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
              rules={{ required: true }}
            />

            <FormField
              control={form.control}
              name='unitPrice'
              render={({ field, fieldState }) => (
                <FormItem className='w-[15%]'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.unitPrice.label'
                      defaultMessage='{unitPriceFieldLabel}'
                      values={{
                        unitPriceFieldLabel: !isPriceWithVat
                          ? unitPriceLabelHT
                          : unitPriceLabelTTC,
                      }}
                    />
                  </FormLabel>
                  <FormControl>
                    <InputPrice
                      {...field}
                      error={fieldState.error}
                      currency={currencyArea.currency}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: true }}
            />

            <FormField
              control={form.control}
              name='quantity'
              render={({ field, fieldState }) => (
                <FormItem className='w-[15%]'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.quantity.label'
                      defaultMessage='Quantité'
                    />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={writeHereLabelPlaceholder}
                      {...field}
                      error={fieldState.error}
                      type='number'
                    />
                  </FormControl>
                </FormItem>
              )}
              rules={{ required: true }}
            />

            <FormField
              control={form.control}
              name='vatRateIndex'
              render={({ field, fieldState }) => (
                <FormItem className='w-[18%]'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.vatRateIndex.label'
                      defaultMessage='TVA'
                    />
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger error={fieldState.error}>
                        <SelectValue placeholder={choseLabelPlaceholder} />
                      </SelectTrigger>

                      <SelectContent className='z-[100000]'>
                        {vatRateOptions.map((vatRateOption, index) => (
                          <SelectItem key={index} value={index.toString()}>
                            {vatRateOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: true }}
            />

            <FormItem className='w-[15%]'>
              <FormLabel>
                <FormattedMessage
                  id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.unitPriceDisabled.label'
                  defaultMessage='{unitPriceDisabledFieldLabel}'
                  values={{
                    unitPriceDisabledFieldLabel: !isPriceWithVat
                      ? unitPriceLabelTTC
                      : unitPriceLabelHT,
                  }}
                />
              </FormLabel>
              <FormControl>
                <InputPrice
                  disabled={true}
                  currency={currencyArea.currency}
                  value={
                    form.formState.errors.totalPrice ? '-' : unitPriceCalculated
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>

            <FormField
              control={form.control}
              name='totalPrice'
              render={({ field, fieldState }) => (
                <FormItem className='w-[15%]'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.totalPrice.label'
                      defaultMessage='{totalPriceFieldLabel}'
                      values={{
                        totalPriceFieldLabel: isPriceWithVat ? (
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.totalPriceHT'
                            defaultMessage='Total HT'
                          />
                        ) : (
                          <FormattedMessage
                            id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.totalPriceTTC'
                            defaultMessage='Total TTC'
                          />
                        ),
                      }}
                    />
                  </FormLabel>
                  <FormControl>
                    <InputPrice
                      {...field}
                      error={fieldState.error}
                      disabled={inputVatRate.length < 2}
                      currency={currencyArea.currency}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{
                required: inputVatRate.length >= 2,
              }}
            />

            <FormField
              control={form.control}
              name='unit'
              render={({ field, fieldState }) => (
                <FormItem className='w-[15%]'>
                  <FormLabel>
                    <FormattedMessage
                      id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.unit.label'
                      defaultMessage='Unité'
                    />
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger error={fieldState.error}>
                        <SelectValue placeholder={choseLabelPlaceholder} />
                      </SelectTrigger>

                      <SelectContent className='z-[100000]'>
                        <SelectItem value={ServiceProvisionUnitEnum.INDIVIDUAL}>
                          {provisionUnitToLabel({
                            unit: ServiceProvisionUnitEnum.INDIVIDUAL,
                            intl,
                          })}
                        </SelectItem>
                        <SelectItem value={ServiceProvisionUnitEnum.GROUP}>
                          {provisionUnitToLabel({
                            unit: ServiceProvisionUnitEnum.GROUP,
                            intl,
                          })}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: true }}
            />
          </div>

          <FormField
            control={form.control}
            name='description'
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage
                    id='ui.quoteSection.createOrUpdateQuoteProvisionModal.field.description.label'
                    defaultMessage='Description'
                  />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={writeHereLabelPlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex h-[50px] items-center gap-3'>
            <TotalPrice
              totalHT={totalPriceHT}
              totalTTC={form.formState.errors.totalPrice ? '-' : totalPriceTTC}
            />
            <div className='flex h-full w-[400px] items-center gap-3'>
              <Button
                variant='outline'
                className='h-full w-full'
                onClick={() => setIsOpen(false)}
              >
                <FormattedMessage
                  id='ui.quoteSection.createOrUpdateQuoteProvisionModal.button.cancel'
                  defaultMessage='Annuler'
                />
              </Button>
              <Button type='submit' className='h-full w-full'>
                <FormattedMessage
                  id='ui.quoteSection.createOrUpdateQuoteProvisionModal.button.save'
                  defaultMessage='Enregistrer'
                />
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  )
}

export default CreateOrUpdateQuoteProvisionModal
